import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { formatEventDateRange } from '@/hooks/useActiveEvent';
import { NPS_PROJECT_ACKNOWLEDGEMENTS, makeEmptyNpsProject } from '@/lib/constants';
import type { NpsProjectEntry } from '@/lib/constants';
import type { EventPublicInfo, EmailCheckResult } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2, ArrowLeft, ArrowRight, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import MilitaryFields from '@/components/registration/MilitaryFields';
import CollaborativeProjectForm from '@/components/registration/CollaborativeProjectForm';
import AcknowledgementSection from '@/components/registration/AcknowledgementSection';
import AthenaOptOutCheckbox from '@/components/registration/AthenaOptOutCheckbox';
import RegistrationConfirmation from '@/components/registration/RegistrationConfirmation';
import { ALL_VALID_RANKS, ALL_SERVICE_BRANCH_VALUES } from '@/lib/military-constants';

type Step = 'personal' | 'project' | 'acknowledgements';

const STEP_LABELS: Record<Step, string> = {
  personal: 'Personal Information',
  project: 'Project Details',
  acknowledgements: 'Acknowledgements',
};

const STEPS: Step[] = ['personal', 'project', 'acknowledgements'];

export default function RegisterNpsProjectPage() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  const { user, isAuthenticated } = useAuth();

  const [event, setEvent] = useState<EventPublicInfo | null>(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [eventError, setEventError] = useState('');

  const [step, setStep] = useState<Step>('personal');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Email check state
  const [emailCheckResult, setEmailCheckResult] = useState<EmailCheckResult | null>(null);
  const lastCheckedEmail = useRef('');

  // Sign-in modal state
  const [showSignIn, setShowSignIn] = useState(false);
  const [showMaxProjectsModal, setShowMaxProjectsModal] = useState(false);
  const [showApprovedMaxModal, setShowApprovedMaxModal] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [signInPassword, setSignInPassword] = useState('');
  const [signInError, setSignInError] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [returningUserVerified, setReturningUserVerified] = useState(false);

  // Step 1: Personal Information
  const [affiliation, setAffiliation] = useState('');
  const [rank, setRank] = useState('');
  const [branchOfService, setBranchOfService] = useState('');
  const [isRetired, setIsRetired] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [department, setDepartment] = useState('');
  const [projectedGraduation, setProjectedGraduation] = useState('');
  const [projectPitchInterest, setProjectPitchInterest] = useState(false);
  const [showcaseTableInterest, setShowcaseTableInterest] = useState(false);

  // Step 2: Project
  const [project, setProject] = useState<NpsProjectEntry>(makeEmptyNpsProject());

  // Step 3: Acknowledgements
  const [acknowledgements, setAcknowledgements] = useState<string[]>([]);
  const [athenaOptOut, setAthenaOptOut] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const url = eventId ? `/events/${eventId}/public` : '/events/active/public';
      const res = await api.get<{ success: boolean; data: EventPublicInfo }>(url);
      if (res.success) {
        if (res.data.status !== 'active') {
          setEventError('Registration for this event is not currently open.');
        } else {
          setEvent(res.data);
        }
      }
    } catch {
      setEventError(
        eventId
          ? 'Event not found. Please check your registration link.'
          : 'No active event found. Please check back later.'
      );
    } finally {
      setLoadingEvent(false);
    }
  };

  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Approved user who has already hit the 3-project cap — nothing left for them to do
  const isApprovedAtCap =
    (emailCheckResult?.hasEventRegistration ?? false) &&
    !emailCheckResult?.hasPendingRegistration &&
    (emailCheckResult?.existingProjectTitles?.length ?? 0) >= 3 &&
    !emailCheckResult?.isRejected;

  // --- Email check on blur ---

  const prefillFromProfile = useCallback((p: EmailCheckResult['profile']) => {
    if (!p) return;

    if (p.firstName && !firstName) setFirstName(p.firstName);
    if (p.lastName && !lastName) setLastName(p.lastName);
    if (p.role && !role) setRole(p.role);
    if (p.department && !department) setDepartment(p.department);
    if (p.rank && ALL_VALID_RANKS.includes(p.rank) && !rank) setRank(p.rank);
    if (p.branchOfService && ALL_SERVICE_BRANCH_VALUES.includes(p.branchOfService) && !branchOfService) setBranchOfService(p.branchOfService);
    if (p.isRetired !== undefined) setIsRetired(p.isRetired);
    if (p.participantType && !affiliation && (p.participantType === 'student' || p.participantType === 'faculty')) setAffiliation(p.participantType);
  }, [firstName, lastName, role, department, rank, branchOfService, isRetired, affiliation]);

  const handleEmailBlur = useCallback(async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) return;
    if (normalizedEmail === lastCheckedEmail.current) return;
    lastCheckedEmail.current = normalizedEmail;

    try {
      const res = await api.post<{ success: boolean; data: EmailCheckResult }>(
        '/register/check-email',
        { email: normalizedEmail, eventId: event?.id },
      );
      if (res.success && res.data.exists) {
        setEmailCheckResult(res.data);

        const isCurrentUser = isAuthenticated && user?.email?.toLowerCase() === normalizedEmail;

        if (res.data.isRejected) {
          setShowRejected(true);
        } else if (res.data.hasAccount && !isCurrentUser) {
          // Different account or not signed in — require sign-in
          setShowSignIn(true);
        } else if (isCurrentUser) {
          // Current user — safe to prefill
          prefillFromProfile(res.data.profile);
          if ((res.data.existingProjectTitles?.length ?? 0) >= 3) {
            if (res.data.hasPendingRegistration) {
              setShowMaxProjectsModal(true);
            } else if (res.data.hasEventRegistration) {
              setShowApprovedMaxModal(true);
            }
          }
        } else if ((res.data.existingProjectTitles?.length ?? 0) >= 3) {
          if (res.data.hasPendingRegistration) {
            setShowMaxProjectsModal(true);
          } else if (res.data.hasEventRegistration) {
            setShowApprovedMaxModal(true);
          }
        }
        // No account (prior registration only) — don't prefill, no auth to verify identity
      } else {
        setEmailCheckResult(null);
      }
    } catch {
      // Silent fail — pre-fill is a convenience, not critical
    }
  }, [email, event?.id, isAuthenticated, prefillFromProfile]);

  const handleSignIn = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!signInPassword) {
      setSignInError('Password is required');
      return;
    }
    setSigningIn(true);
    setSignInError('');
    try {
      const res = await api.post<{
        user: Record<string, unknown>;
        accessToken: string;
        expiresIn: number;
      }>('/auth/registration-login', { email: normalizedEmail, password: signInPassword });
      api.setAuthTokens({ accessToken: res.accessToken, expiresIn: res.expiresIn });
      setShowSignIn(false);
      setSignInPassword('');
      setReturningUserVerified(true);
      // Pre-fill form from the returned user profile
      const p = res.user;
      if (p.firstName) setFirstName(p.firstName as string);
      if (p.lastName) setLastName(p.lastName as string);
      if (p.role) setRole(p.role as string);
      if (p.department) setDepartment(p.department as string);
      if (typeof p.rank === 'string' && ALL_VALID_RANKS.includes(p.rank)) setRank(p.rank);
      if (typeof p.branchOfService === 'string' && ALL_SERVICE_BRANCH_VALUES.includes(p.branchOfService)) setBranchOfService(p.branchOfService);
      if (p.isRetired !== undefined) setIsRetired(p.isRetired as boolean);
      const pt = p.participantType as string;
      if (pt === 'student' || pt === 'faculty') setAffiliation(pt);
      if ((emailCheckResult?.existingProjectTitles?.length ?? 0) >= 3 && !emailCheckResult?.isRejected) {
        if (emailCheckResult?.hasPendingRegistration) {
          setShowMaxProjectsModal(true);
        } else if (emailCheckResult?.hasEventRegistration) {
          setShowApprovedMaxModal(true);
        }
      }
    } catch {
      setSignInError('Invalid email or password. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  // --- Validation ---

  const validatePersonal = (): boolean => {
    const errs: Record<string, string> = {};

    if (!affiliation) errs.affiliation = 'Affiliation is required.';
    if (!firstName.trim()) errs.firstName = 'First name is required.';
    if (!lastName.trim()) errs.lastName = 'Last name is required.';
    if (!role.trim()) errs.role = 'Position is required.';
    if (!email.trim()) {
      errs.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.email = 'Please enter a valid email address.';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateProject = (): boolean => {
    const errs: Record<string, string> = {};

    if (!project.title.trim()) errs['project0.title'] = 'Project title is required.';
    if (!project.description.trim()) errs['project0.description'] = 'Project description is required.';
    if (project.seekingCollaboration.length === 0) errs['project0.seekingCollaboration'] = 'Select at least one collaboration type.';
    if (!project.classification) errs['project0.classification'] = 'Classification is required.';
    if (!project.stage) errs['project0.stage'] = 'Development stage is required.';
    if (!project.fundingStatus) errs['project0.fundingStatus'] = 'Funding status is required.';
    if (project.technologyAreas.length === 0) errs['project0.technologyAreas'] = 'Select at least one technology area.';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateAcknowledgements = (): boolean => {
    if (acknowledgements.length < NPS_PROJECT_ACKNOWLEDGEMENTS.length) {
      setErrors({ acknowledgements: 'All acknowledgements must be accepted.' });
      return false;
    }
    setErrors({});
    return true;
  };

  // --- Navigation ---

  const goToProject = () => {
    const isAlreadyAuthenticated = isAuthenticated && user?.email?.toLowerCase() === email.trim().toLowerCase();
    if (emailCheckResult?.hasAccount && !returningUserVerified && !isAlreadyAuthenticated) {
      setShowSignIn(true);
      return;
    }
    if (emailCheckResult?.isRejected) {
      setShowRejected(true);
      return;
    }
    if (isApprovedAtCap) {
      setShowApprovedMaxModal(true);
      return;
    }
    if (emailCheckResult?.hasPendingRegistration && (emailCheckResult?.existingProjectTitles?.length ?? 0) >= 3) {
      setShowMaxProjectsModal(true);
      return;
    }
    if (validatePersonal()) setStep('project');
  };

  const goToAcknowledgements = () => {
    if (isApprovedAtCap) {
      setShowApprovedMaxModal(true);
      return;
    }
    const alreadySubmittedCount = emailCheckResult?.existingProjectTitles?.length ?? 0;
    if (alreadySubmittedCount >= 3 || validateProject()) setStep('acknowledgements');
  };

  const goBack = () => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) {
      setErrors({});
      setStep(STEPS[idx - 1]!);
    }
  };

  // --- Submit ---

  const handleSubmit = async () => {
    const isAlreadyAuthenticated = isAuthenticated && user?.email?.toLowerCase() === email.trim().toLowerCase();
    if (emailCheckResult?.hasAccount && !returningUserVerified && !isAlreadyAuthenticated) {
      setErrors({ submit: 'Please sign in to submit as a returning attendee.' });
      setStep('personal');
      setShowSignIn(true);
      return;
    }
    const alreadySubmittedCount = emailCheckResult?.existingProjectTitles?.length ?? 0;
    if (!validatePersonal()) {
      setStep('personal');
      return;
    }
    if (alreadySubmittedCount < 3 && !validateProject()) {
      setStep('project');
      return;
    }
    if (!validateAcknowledgements()) return;

    setSubmitting(true);
    try {
      await api.post('/register', {
        registrationType: 'nps_project',
        eventId: event?.id,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        participantType: affiliation,
        rank: rank || undefined,
        branchOfService: branchOfService || undefined,
        isRetired,
        role: role.trim(),
        department: department.trim() || undefined,
        projectedGraduation: projectedGraduation.trim() || undefined,
        projectPitchInterest,
        showcaseTableInterest,
        athenaOptOut,
        acknowledgements,
        projectData: [project],
      });
      setSubmitted(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      const msg = error?.response?.data?.error || 'Failed to submit. Please try again.';
      setErrors({ submit: msg });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render states ---

  if (loadingEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (eventError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-3">
        <div className="text-center space-y-3">
          <h1 className="text-2xl font-bold">Registration Unavailable</h1>
          <p className="text-muted-foreground">{eventError}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <RegistrationConfirmation registrationType="nps_project" />
      </div>
    );
  }

  const stepNumber = STEPS.indexOf(step) + 1;
  const totalSteps = STEPS.length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-navy text-white py-10 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-5">
          <img src="/nps-logo.png" alt="NPS Foundation" className="h-14 w-auto shrink-0" />
          <div className="space-y-1">
            <h1 className="text-2xl md:text-3xl font-bold">Converge @ NPS</h1>
            <p className="text-sm text-blue-200">
              {event ? formatEventDateRange(event.dateStart, event.dateEnd) : ''} &middot; Project Submission
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Step indicator */}
        <div className="text-center mb-8">
          <div className="flex justify-center gap-2">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i + 1 <= stepNumber ? 'bg-primary w-10' : 'bg-muted w-8'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Step {stepNumber} of {totalSteps}: {STEP_LABELS[step]}
          </p>
        </div>

        {/* Step 1: Personal Information */}
        {step === 'personal' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Personal Information</h2>

            {/* Email — first so returning users get immediate feedback */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">POC Email *</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearError('email');
                  if (returningUserVerified) setReturningUserVerified(false);
                  lastCheckedEmail.current = '';
                }}
                onBlur={handleEmailBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                placeholder="email@nps.edu"
                className={`h-10 ${errors.email ? 'border-destructive' : ''}`}
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
              {emailCheckResult?.exists && emailCheckResult.hasPendingRegistration && !showSignIn && (emailCheckResult.existingProjectTitles?.length ?? 0) < 3 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                  You already have a pre-registration for this event. Your status is pending admin approval. You can still submit an additional project — it will be added to your existing pre-registration.
                </div>
              )}
              {emailCheckResult?.exists && emailCheckResult.hasEventRegistration && !emailCheckResult.hasPendingRegistration && !emailCheckResult.isRejected && (emailCheckResult.existingProjectTitles?.length ?? 0) < 3 && (
                <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-600 dark:bg-blue-950 dark:text-blue-200">
                  You already have a registration for this event. You can still submit an additional project — it will be added to your existing registration.
                </div>
              )}
              {emailCheckResult?.exists && !emailCheckResult.hasEventRegistration && emailCheckResult.hasAccount && isAuthenticated && user?.email?.toLowerCase() === email.trim().toLowerCase() && (
                <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-800 dark:border-green-600 dark:bg-green-950 dark:text-green-200">
                  Signed in — your details have been pre-filled below. Please review and update as needed.
                </div>
              )}
            </div>

            {/* Affiliation */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Affiliation *</label>
              <Select
                value={affiliation}
                onValueChange={(v) => {
                  setAffiliation(v);
                  clearError('affiliation');
                }}
              >
                <SelectTrigger className={`h-10 ${errors.affiliation ? 'border-destructive' : ''}`}>
                  <SelectValue placeholder="Select affiliation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="faculty">Faculty</SelectItem>
                </SelectContent>
              </Select>
              {errors.affiliation && <p className="text-xs text-destructive">{errors.affiliation}</p>}
            </div>

            {/* Military Rank & Branch */}
            <MilitaryFields
              branchOfService={branchOfService}
              rank={rank}
              isRetired={isRetired}
              onBranchChange={(v) => { setBranchOfService(v); setRank(''); }}
              onRankChange={setRank}
              onRetiredChange={setIsRetired}
            />

            {/* First Name & Last Name */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">POC First Name *</label>
                <Input
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    clearError('firstName');
                  }}
                  placeholder="First name"
                  className={`h-10 ${errors.firstName ? 'border-destructive' : ''}`}
                />
                {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">POC Last Name *</label>
                <Input
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    clearError('lastName');
                  }}
                  placeholder="Last name"
                  className={`h-10 ${errors.lastName ? 'border-destructive' : ''}`}
                />
                {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
              </div>
            </div>

            {/* Position / Role */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">POC Position *</label>
              <Input
                value={role}
                onChange={(e) => {
                  setRole(e.target.value);
                  clearError('role');
                }}
                placeholder="e.g. NPS student, Associate Professor"
                className={`h-10 ${errors.role ? 'border-destructive' : ''}`}
              />
              <p className="text-xs text-muted-foreground">
                If NPS faculty, provide position. If student, use &quot;NPS student.&quot;
              </p>
              {errors.role && <p className="text-xs text-destructive">{errors.role}</p>}
            </div>

            {/* Department */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">NPS Program of Study / Department</label>
              <Input
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="e.g. Computer Science, Defense Analysis"
                className="h-10"
              />
            </div>

            {/* Projected Graduation */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Projected Graduation</label>
              <Input
                value={projectedGraduation}
                onChange={(e) => setProjectedGraduation(e.target.value)}
                placeholder="e.g. September 2026"
                className="h-10"
              />
            </div>

            <p className="text-sm font-bold text-foreground">
              How do you want to participate? Select all that apply.
            </p>

            {/* Project Pitch Interest */}
            <label className="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 border-border hover:border-primary/50">
              <Checkbox
                checked={projectPitchInterest}
                onCheckedChange={(checked) => setProjectPitchInterest(!!checked)}
                className="h-4 w-4 mt-0.5 shrink-0"
              />
              <div>
                <span className="text-sm font-medium">Interested in Live Project Pitch</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  The Project Pitch event is an opportunity to pitch your project to industry partners, government sponsors, and military leaders for potential collaboration, funding, or partnership.
                </p>
              </div>
            </label>

            {/* Showcase Table Interest */}
            <label className="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 border-border hover:border-primary/50">
              <Checkbox
                checked={showcaseTableInterest}
                onCheckedChange={(checked) => setShowcaseTableInterest(!!checked)}
                className="h-4 w-4 mt-0.5 shrink-0"
              />
              <div>
                <span className="text-sm font-medium">Interested in Showcase Table</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Best for projects with digital or physical prototypes or demos. A showcase table provides space to demonstrate your project during the event.
                </p>
              </div>
            </label>

            {Object.keys(errors).filter((k) => k !== 'submit').length > 0 && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                Please fix the highlighted fields above before continuing.
              </div>
            )}

            {/* Navigation */}
            <div className="flex gap-2 pt-2">
              {emailCheckResult?.isRejected ? (
                <div className="flex-1 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-center text-destructive">
                  Your registration was not approved. Check your email for a resubmit link.
                </div>
              ) : isApprovedAtCap ? (
                <div className="flex-1 rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-center text-green-800 dark:border-green-600 dark:bg-green-950 dark:text-green-200">
                  You have been approved and have submitted the maximum number of projects.
                </div>
              ) : emailCheckResult?.hasPendingRegistration && (emailCheckResult?.existingProjectTitles?.length ?? 0) >= 3 ? (
                <div className="flex-1 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-center text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                  Your pre-registration is pending admin approval.
                </div>
              ) : (
                <Button onClick={goToProject} className="flex-1 h-11 md:h-10">
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Project */}
        {step === 'project' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Project Details</h2>

            {(() => {
              const alreadySubmittedCount = emailCheckResult?.existingProjectTitles?.length ?? 0;
              const canSubmitMore = alreadySubmittedCount < 3;
              return (
                <>
                  {alreadySubmittedCount > 0 && (
                    <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-600 dark:bg-blue-950 dark:text-blue-200">
                      <p className="font-medium mb-1">Previously submitted {alreadySubmittedCount === 1 ? 'project' : 'projects'}:</p>
                      <ul className="list-disc list-inside">
                        {emailCheckResult!.existingProjectTitles.map((title, i) => (
                          <li key={i}>{title}</li>
                        ))}
                      </ul>
                      {canSubmitMore && (
                        <p className="mt-1.5 text-blue-700 dark:text-blue-300">Submit a new project below — it will be added to your existing registration.</p>
                      )}
                    </div>
                  )}

                  {canSubmitMore ? (
                    <CollaborativeProjectForm
                      variant="nps"
                      project={project}
                      onChange={(p) => setProject(p as NpsProjectEntry)}
                      index={alreadySubmittedCount}
                      errors={errors}
                    />
                  ) : (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                      You have already submitted the maximum of 3 projects for this event. No additional projects can be added.
                    </div>
                  )}
                </>
              );
            })()}

            {/* Navigation */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={goBack} className="flex-1 h-11 md:h-10">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              {(emailCheckResult?.existingProjectTitles?.length ?? 0) < 3 && (
                <Button onClick={goToAcknowledgements} className="flex-1 h-11 md:h-10">
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Acknowledgements */}
        {step === 'acknowledgements' && (
          <div className="space-y-6">
            <AcknowledgementSection
              acknowledgements={NPS_PROJECT_ACKNOWLEDGEMENTS}
              accepted={acknowledgements}
              onChange={setAcknowledgements}
              error={errors.acknowledgements}
            />
            <AthenaOptOutCheckbox
              checked={athenaOptOut}
              onChange={setAthenaOptOut}
            />

            {Object.keys(errors).filter((k) => k !== 'submit').length > 0 && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                Please fix the highlighted fields above before continuing.
              </div>
            )}

            {errors.submit && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {errors.submit}
              </div>
            )}

            {/* Navigation */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={goBack} className="flex-1 h-11 md:h-10" disabled={submitting}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button onClick={handleSubmit} className="flex-1 h-11 md:h-10" disabled={submitting}>
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                ) : (
                  'Submit Project'
                )}
              </Button>
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center mt-8">
          Questions? Email{' '}
          <a href="mailto:converge@npsfoundation.org" className="text-primary underline">
            converge@npsfoundation.org
          </a>
        </p>
      </div>

      {/* Sign-in modal for existing accounts */}
      <Dialog open={showSignIn} onOpenChange={(open) => { if (!signingIn) setShowSignIn(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sign in to continue</DialogTitle>
            <DialogDescription>
              An account with <strong>{email}</strong> exists from previous events. Please sign in here, then continue completing the registration form (double-check all pre-populated fields).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Email</Label>
              <Input value={email} disabled className="bg-muted" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Password</Label>
              <Input
                type="password"
                value={signInPassword}
                onChange={(e) => setSignInPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSignIn(); }}
                placeholder="Enter your password"
                autoFocus
              />
            </div>
            {signInError && (
              <p className="text-sm text-destructive">{signInError}</p>
            )}
            <div className="flex flex-col gap-2">
              <Button onClick={handleSignIn} disabled={signingIn} className="w-full">
                {signingIn ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Signing in...</> : 'Sign In'}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Forgot your password?{' '}
                <Link to="/forgot-password?redirect=/register/nps-project" className="text-primary hover:underline">
                  Click here to reset it
                </Link>
                . Then return to the registration page to continue registering for the event. You must submit the registration form in order to attend the current event.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approved-max modal — approved attendee has already hit the 3-project cap */}
      <Dialog open={showApprovedMaxModal} onOpenChange={setShowApprovedMaxModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-1 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center dark:bg-green-900">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <DialogTitle className="text-center">Pre-Registration Approved</DialogTitle>
            <DialogDescription className="text-center">
              You have been approved for this event and have already submitted the maximum number of projects (3). There is nothing left to do on this page.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowApprovedMaxModal(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Max projects modal — returning attendee already has 3 projects pending approval */}
      <Dialog open={showMaxProjectsModal} onOpenChange={setShowMaxProjectsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-1 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center dark:bg-amber-900">
              <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <DialogTitle className="text-center">Pre-Registration Pending Admin Approval</DialogTitle>
            <DialogDescription className="text-center">
              You already have submitted a pre-registration for this event. It is pending admin approval. You have also submitted the maximum number of projects (3). There is nothing left to do on this page.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowMaxProjectsModal(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rejected modal — registration was not approved */}
      <Dialog open={showRejected} onOpenChange={setShowRejected}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-1 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <DialogTitle className="text-center">Registration Not Approved</DialogTitle>
            <DialogDescription className="text-center">
              Your registration for this event was not approved. Check your email for a resubmit link to try again.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowRejected(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
